/**
 * Trip Date Consistency Analyzer
 * 
 * Only flags unambiguous data-entry errors:
 *   - A day-rate period whose end date is before its start date.
 *
 * Gap and overlap detection has been removed because real-world
 * seafarer trip patterns (hotel transitions, UK-waters multi-leg
 * crossings, transit days, varied embarkation sequences) produce
 * too many false positives to be useful.
 */
import { parseISO, format } from 'date-fns';

const safeParse = (str) => {
  if (!str) return null;
  try { return parseISO(str); } catch (err) { return null; }
};

const fmtDate = (d) => format(d, 'dd MMM yyyy');

/**
 * Analyze a trip for date consistency issues.
 * @returns {{ gaps: Array, overlaps: Array, orderIssues: Array, hasWarnings: boolean }}
 */
export const analyzeTripDates = (trip) => {
  if (!trip) return { gaps: [], overlaps: [], orderIssues: [], hasWarnings: false };

  // Only check day_rate work trips that have rate entries
  if (trip.pay_type !== 'day_rate') {
    return { gaps: [], overlaps: [], orderIssues: [], hasWarnings: false };
  }

  const dayRates = trip.day_rates || [];
  if (dayRates.length === 0) {
    return { gaps: [], overlaps: [], orderIssues: [], hasWarnings: false };
  }

  const orderIssues = [];

  // Flag day-rate periods where end is before start (clear data-entry error)
  for (let i = 0; i < dayRates.length; i++) {
    const from = safeParse(dayRates[i].date_from);
    const to = safeParse(dayRates[i].date_to);
    if (from && to && to < from) {
      orderIssues.push(
        `Day rate period ${i + 1}: end date (${fmtDate(to)}) is before start date (${fmtDate(from)})`
      );
    }
  }

  return {
    gaps: [],
    overlaps: [],
    orderIssues,
    hasWarnings: orderIssues.length > 0
  };
};
